"use client"

import { useState, useMemo, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { Card } from "@/components/ui/card"
import { Calendar, MapPin, Users, CalendarPlus, List, LayoutGrid } from "lucide-react"

interface Activity {
  name: string
  startTime: string
  endTime: string
  location?: string
  description?: string
  speaker?: string
  track?: "fgd" | "ladies"
}

interface EventDay {
  date: string
  dateFormatted: string
  shortLabel: string
  activities: Activity[]
}

const getEventDays = (isID: boolean): EventDay[] => [
  {
    date: "27-08-2026",
    dateFormatted: isID ? "Kamis - Jumat, 27 & 28 Agustus 2026" : "Thursday - Friday, August 27 & 28, 2026",
    shortLabel: isID ? "27-28 Ags" : "Aug 27-28",
    activities: [
      {
        name: isID ? "PEMBUKAAN DARING" : "ONLINE OPENING",
        startTime: "09:00",
        endTime: "09:30",
        location: isID ? "Platform Daring (Hybrid)" : "Online Platform (Hybrid)",
        description: isID
          ? "Pembukaan secara daring untuk peserta dari berbagai daerah"
          : "Online opening ceremony for participants across various regions",
      },
      {
        name: "KEYNOTE SPEAKER",
        startTime: "09:30",
        endTime: "12:30",
        location: isID ? "Platform Daring (Hybrid)" : "Online Platform (Hybrid)",
        description: isID
          ? "Sesi pemaparan oleh 4 pembicara utama dengan topik strategis"
          : "Presentation sessions by 4 keynote speakers covering strategic topics",
      },
      {
        name: isID ? "ISTIRAHAT" : "BREAK",
        startTime: "12:30",
        endTime: "13:30",
        location: isID ? "Platform Daring" : "Online Platform",
      },
      {
        name: "CONFERENCE @TRACK",
        startTime: "13:30",
        endTime: "17:00",
        location: isID ? "Ruang-ruang Track Konferensi (Daring)" : "Conference Track Rooms (Online)",
        description: isID
          ? "Sesi konferensi terpilih sesuai track minat peserta"
          : "Selected conference sessions according to participants' chosen tracks",
      },
    ],
  },
  {
    date: "01-10-2026",
    dateFormatted: isID ? "Kamis, 1 Oktober 2026" : "Thursday, October 1, 2026",
    shortLabel: isID ? "Kam, 1 Okt" : "Thu, Oct 1",
    activities: [
      {
        name: isID ? "KEDATANGAN PESERTA" : "PARTICIPANT ARRIVAL",
        startTime: "08:00",
        endTime: "17:00",
        location: "NDCC (Nusa Dua Convention Center) - Bali",
        description: isID
          ? "Registrasi dan welcome check-in peserta konferensi"
          : "Registration and welcome check-in for conference participants",
      },
    ],
  },
  {
    date: "02-10-2026",
    dateFormatted: isID ? "Jumat, 2 Oktober 2026" : "Friday, October 2, 2026",
    shortLabel: isID ? "Jum, 2 Okt" : "Fri, Oct 2",
    activities: [
      {
        name: isID ? "REGISTRASI" : "REGISTRATION",
        startTime: "15:30",
        endTime: "16:00",
        location: "NDCC - Nusa Dua Hall",
      },
      {
        name: isID ? "KUDAPAN" : "REFRESHMENTS",
        startTime: "16:00",
        endTime: "16:30",
        location: "NDCC - Nusa Dua Hall",
      },
      {
        name: isID ? "MENYANYIKAN LAGU INDONESIA RAYA" : "INDONESIA RAYA ANTHEM",
        startTime: "16:30",
        endTime: "16:35",
        location: "NDCC - Nusa Dua Hall",
      },
      {
        name: isID ? "DOA" : "PRAYER",
        startTime: "16:35",
        endTime: "16:40",
        location: "NDCC - Nusa Dua Hall",
      },
      {
        name: isID ? "PEMBUKAAN" : "OPENING",
        startTime: "16:40",
        endTime: "16:45",
        location: "NDCC - Nusa Dua Hall",
        speaker: "MC",
      },
      {
        name: isID ? "TARI KECAK - SUNAR LEGONG" : "KECAK - SUNAR LEGONG DANCE",
        startTime: "16:45",
        endTime: "16:55",
        location: "NDCC - Nusa Dua Hall",
        description: isID
          ? "Pergelaran seni tari tradisional Bali"
          : "Traditional Balinese dance performance",
      },
      {
        name: isID ? "SAMBUTAN REKTOR UNDIKSHA" : "REMARKS BY RECTOR OF UNDIKSHA",
        startTime: "16:55",
        endTime: "17:15",
        location: "NDCC - Nusa Dua Hall",
        speaker: "Prof. Dr. I Wayan Lasmawan, M.Pd.",
      },
      {
        name: isID
          ? "SAMBUTAN MENTERI PENDIDIKAN TINGGI, SAINS, DAN TEKNOLOGI RI SEKALIGUS ARAH KEBIJAKAN"
          : "REMARKS BY THE MINISTER OF HIGHER EDUCATION, SCIENCE, AND TECHNOLOGY AND POLICY DIRECTION",
        startTime: "17:15",
        endTime: "17:55",
        location: "NDCC - Nusa Dua Hall",
        description: isID
          ? "Dilanjutkan dengan pemasangan puzzle sebagai simbol pembukaan"
          : "Followed by puzzle assembly as the opening symbol",
        speaker: isID
          ? "Menteri Pendidikan Tinggi, Sains, dan Teknologi RI (Prof. Brian Yuliarto, Ph.D.)"
          : "Minister of Higher Education, Science, and Technology (Prof. Brian Yuliarto, Ph.D.)",
      },
      {
        name: isID ? "FOTO BERSAMA" : "GROUP PHOTO",
        startTime: "17:55",
        endTime: "18:00",
        location: "NDCC - Nusa Dua Hall",
      },
      {
        name: isID ? "ARAH KEBIJAKAN KEMENTERIAN PENDIDIKAN DASAR DAN MENENGAH" : "POLICY DIRECTION OF THE MINISTRY OF PRIMARY AND SECONDARY EDUCATION",
        startTime: "18:00",
        endTime: "18:30",
        location: "NDCC - Nusa Dua Hall",
        speaker: isID
          ? "Menteri Pendidikan Dasar dan Menengah RI (Prof. Dr. Abdul Mu'ti, M.Ed.)"
          : "Minister of Primary and Secondary Education (Prof. Dr. Abdul Mu'ti, M.Ed.)",
      },
      {
        name: isID ? "ARAH KEBIJAKAN KEMENTERIAN KEBUDAYAAN" : "POLICY DIRECTION OF THE MINISTRY OF CULTURE",
        startTime: "18:30",
        endTime: "19:00",
        location: "NDCC - Nusa Dua Hall",
        speaker: isID
          ? "Menteri Kebudayaan RI (Fadli Zon, S.S., M.Sc.)"
          : "Minister of Culture (Fadli Zon, S.S., M.Sc.)",
      },
      {
        name: isID ? "KUNJUNGAN PAMERAN" : "EXHIBITION VISIT",
        startTime: "19:00",
        endTime: "19:30",
        location: isID ? "NDCC - Area Pameran" : "NDCC - Exhibition Area",
      },
      {
        name: isID ? "MAKAN MALAM" : "GALA DINNER",
        startTime: "19:30",
        endTime: "21:00",
        location: "NDCC - Nusa Dua Hall",
      },
    ],
  },
  {
    date: "03-10-2026",
    dateFormatted: isID ? "Sabtu, 3 Oktober 2026" : "Saturday, October 3, 2026",
    shortLabel: isID ? "Sab, 3 Okt" : "Sat, Oct 3",
    activities: [
      {
        name: isID ? "REGISTRASI (KONVENSI)" : "REGISTRATION (CONVENTION)",
        startTime: "08:00",
        endTime: "08:30",
        location: "NDCC - Main Hall",
      },
      {
        name: isID ? "PENDIDIKAN DIGITAL DAN KECERDASAN ARTIFISIAL" : "DIGITAL EDUCATION AND ARTIFICIAL INTELLIGENCE",
        startTime: "08:30",
        endTime: "09:00",
        location: "NDCC - Main Hall",
        description: isID
          ? "Mempersiapkan SDM Tangguh di Era Revolusi Industri 5.0"
          : "Preparing Resilient Human Resources in the Industrial Revolution 5.0 Era",
        speaker: isID
          ? "Dirjen Sains dan Teknologi (Dr. Ahmad Najib Burhani, M.A.)"
          : "Director General of Science and Technology (Dr. Ahmad Najib Burhani, M.A.)",
      },
      {
        name: isID ? "PENDIDIKAN INKLUSIF DAN KURIKULUM TRANSFORMATIF" : "INCLUSIVE EDUCATION AND TRANSFORMATIVE CURRICULUM",
        startTime: "09:00",
        endTime: "09:30",
        location: "NDCC - Main Hall",
        description: isID
          ? "Menjangkau Seluruh Lapisan Masyarakat dari Kota hingga Pelosok Negeri"
          : "Reaching All Layers of Society from Cities to Remote Areas of the Nation",
        speaker: isID
          ? "Direktur Pembelajaran dan Kemahasiswaan / Belmawa (Dr. Beny Bandanadjaja, S.T., M.T.)"
          : "Director of Learning and Student Affairs / Belmawa (Dr. Beny Bandanadjaja, S.T., M.T.)",
      },
      {
        name: isID ? "REVITALISASI PERAN GURU" : "REVITALIZATION OF THE ROLE OF TEACHERS",
        startTime: "09:30",
        endTime: "10:00",
        location: "NDCC - Main Hall",
        description: isID
          ? "Penguatan Kompetensi, Kesejahteraan, dan Kepemimpinan Pendidik Abad 21"
          : "Strengthening Competence, Welfare, and Leadership of 21st-Century Educators",
        speaker: isID
          ? "Dirjen Guru dan Tenaga Kependidikan (Prof. Dr. Nunuk Suryani, M.Pd.)"
          : "Director General of Teachers and Education Personnel (Prof. Dr. Nunuk Suryani, M.Pd.)",
      },
      {
        name: isID ? "DISKUSI" : "DISCUSSION",
        startTime: "10:00",
        endTime: "10:30",
        location: "NDCC - Main Hall",
      },
      {
        name: "GREEN EDUCATION",
        startTime: "10:30",
        endTime: "11:00",
        location: "NDCC - Main Hall",
        description: isID
          ? "Pendidikan Berkelanjutan untuk Membangun Kesadaran Lingkungan dan Ketahanan Iklim"
          : "Sustainable Education to Build Environmental Awareness and Climate Resilience",
        speaker: isID
          ? "Wakil Rektor IV Universitas Indonesia (Prof. Agus Setiawan, S.Kp., M.N., D.N.)"
          : "Vice Rector IV of Universitas Indonesia (Prof. Agus Setiawan, S.Kp., M.N., D.N.)",
      },
      {
        name: isID ? "SINERGI TRI PUSAT PENDIDIKAN" : "SYNERGY OF THE THREE EDUCATIONAL CENTERS",
        startTime: "11:00",
        endTime: "11:30",
        location: "NDCC - Main Hall",
        description: isID
          ? "Kolaborasi Sekolah, Keluarga, dan Masyarakat dalam Membentuk Karakter Generasi Pancasila"
          : "Collaboration of School, Family, and Community in Shaping the Pancasila Generation Character",
        speaker: isID
          ? "Rektor Universitas Pendidikan Indonesia (Prof. Dr. H. Didi Sukyadi, M.A.)"
          : "Rector of Universitas Pendidikan Indonesia (Prof. Dr. H. Didi Sukyadi, M.A.)",
      },
      {
        name: isID ? "DISKUSI" : "DISCUSSION",
        startTime: "11:30",
        endTime: "12:00",
        location: "NDCC - Main Hall",
      },
      {
        name: isID ? "KUNJUNGAN PAMERAN" : "EXHIBITION VISIT",
        startTime: "12:00",
        endTime: "12:30",
        location: isID ? "NDCC - Area Pameran" : "NDCC - Exhibition Area",
      },
      {
        name: "ISHOMA",
        startTime: "12:30",
        endTime: "13:30",
        location: "NDCC",
      },
      {
        name: isID ? "TATA KELOLA PERGURUAN TINGGI YANG ADAPTIF DAN INOVATIF" : "ADAPTIVE AND INNOVATIVE HIGHER EDUCATION GOVERNANCE",
        startTime: "13:30",
        endTime: "14:30",
        location: "NDCC - Main Hall",
        description: isID
          ? "Strategi Akselerasi Menuju Indonesia Emas 2045"
          : "Acceleration Strategy toward Golden Indonesia 2045",
        speaker: isID
          ? "Ketua Majelis Rektor Perguruan Tinggi Negeri (Prof. Dr. Ir. Eduart Wolok, ST., MT., IPU., ASEAN.Eng.) dan Ketua Forum Rektor Indonesia (Prof. Dr. Garuda Wiko, S.H., M.Si.)"
          : "Chairman of MRPTNI (Prof. Dr. Ir. Eduart Wolok, ST., MT., IPU., ASEAN.Eng.) and Chairman of FRI (Prof. Dr. Garuda Wiko, S.H., M.Si.)",
      },
      {
        name: isID ? "PENGUATAN LPTK" : "STRENGTHENING OF LPTK",
        startTime: "14:30",
        endTime: "15:30",
        location: "NDCC - Main Hall",
        description: isID
          ? "Menjaga Mutu Pendidikan, Menggerakkan Transformasi, dan Mewujudkan Dampak Menuju Indonesia Emas 2045"
          : "Maintaining Education Quality, Driving Transformation, and Delivering Impact toward Golden Indonesia 2045",
        speaker: "Prof. Drs. Ganefri, M.Pd., Ph.D. & Prof. Dr. AH. Rofi'uddin, M.Pd.",
      },
      {
        name: isID ? "DISKUSI" : "DISCUSSION",
        startTime: "15:30",
        endTime: "16:00",
        location: "NDCC - Main Hall",
      },
      {
        name: isID ? "KUDAPAN" : "REFRESHMENTS",
        startTime: "16:00",
        endTime: "16:30",
        location: "NDCC",
      },
      {
        name: isID ? "ISTIRAHAT" : "REST",
        startTime: "16:30",
        endTime: "17:00",
        location: "NDCC",
      },
      {
        name: isID ? "TARI JAYANING NATENG JAGAT" : "JAYANING NATENG JAGAT DANCE",
        startTime: "17:00",
        endTime: "17:15",
        location: "NDCC - Main Hall",
      },
      {
        name: isID ? "PENYAMPAIAN HASIL KONASPI" : "PRESENTATION OF KONASPI RESULTS",
        startTime: "17:15",
        endTime: "17:45",
        location: "NDCC - Main Hall",
        speaker: isID
          ? "Prof. Dr. I Wayan Lasmawan, M.Pd. dan Para Rektor LPTKNI"
          : "Prof. Dr. I Wayan Lasmawan, M.Pd. and LPTKNI Rectors",
      },
      {
        name: isID ? "PENYERAHAN BENDERA KONASPI" : "HANDOVER OF THE KONASPI FLAG",
        startTime: "17:45",
        endTime: "18:00",
        location: "NDCC - Main Hall",
        description: isID
          ? "Penyerahan bendera Konaspi kepada tuan rumah berikutnya"
          : "Handover of the Konaspi flag to the next host",
        speaker: isID
          ? "Prof. Dr. I Wayan Lasmawan, M.Pd. dan Para Rektor LPTKNI"
          : "Prof. Dr. I Wayan Lasmawan, M.Pd. and LPTKNI Rectors",
      },
      {
        name: isID ? "PENUTUPAN KONASPI XII" : "CLOSING OF KONASPI XII",
        startTime: "18:00",
        endTime: "18:15",
        location: "NDCC - Main Hall",
        description: isID
          ? "Ditandai dengan pemukulan kulkul"
          : "Marked by the striking of the kulkul (traditional Balinese bell)",
        speaker: isID
          ? "Rektor Universitas Pendidikan Ganesha (Prof. Dr. I Wayan Lasmawan, M.Pd.)"
          : "Rector of Universitas Pendidikan Ganesha (Prof. Dr. I Wayan Lasmawan, M.Pd.)",
      },
      {
        name: isID ? "MAKAN MALAM" : "DINNER",
        startTime: "18:15",
        endTime: "19:30",
        location: "NDCC",
      },
      {
        name: isID ? "REGISTRASI (FGD)" : "REGISTRATION (FGD)",
        startTime: "08:00",
        endTime: "08:30",
        location: isID ? "Masing-masing Room" : "Respective Rooms",
        track: "fgd",
      },
      {
        name: isID ? "[FGD 1] STRATEGI PENGEMBANGAN KURIKULUM TRANSFORMATIF" : "[FGD 1] TRANSFORMATIVE CURRICULUM DEVELOPMENT STRATEGY",
        startTime: "08:30",
        endTime: "12:30",
        location: "NDCC - Uluwatu 1",
        track: "fgd",
        description: isID
          ? "Presentasi & diskusi kurikulum transformatif yang mudah diadaptasi terhadap perubahan konteks lokal dan global, dilanjutkan perumusan draft policy brief"
          : "Presentation and discussion on transformative curriculum adaptable to local and global context changes, followed by drafting a policy brief",
        speaker: isID
          ? "Dirjen PAUD, Dikdas, dan Dikmen"
          : "Director General of Early Childhood, Primary, and Secondary Education",
      },
      {
        name: isID ? "[FGD 2] KURIKULUM PT RESPONSIF INDUSTRI" : "[FGD 2] INDUSTRY-RESPONSIVE HIGHER EDUCATION CURRICULUM",
        startTime: "08:30",
        endTime: "12:30",
        location: "NDCC - Uluwatu 2",
        track: "fgd",
        description: isID
          ? "Presentasi & diskusi pengembangan kurikulum PT yang responsif terhadap kebutuhan industri dan pasar kerja, dilanjutkan perumusan draft policy brief"
          : "Presentation and discussion on higher education curriculum responsive to industry and job market needs, followed by drafting a policy brief",
        speaker: isID
          ? "Guru Besar dan Dosen Departemen Teknik Fisika, Institut Teknologi Sepuluh Nopember (Prof. Dr. Ir. Syamsul Arifin, M.T.)"
          : "Professor and Lecturer, Department of Engineering Physics, Institut Teknologi Sepuluh Nopember (Prof. Dr. Ir. Syamsul Arifin, M.T.)",
      },
      {
        name: isID ? "[FGD 3] ANALISIS KRITIS RUU SISDIKNAS" : "[FGD 3] CRITICAL ANALYSIS OF THE SISDIKNAS BILL",
        startTime: "08:30",
        endTime: "12:30",
        location: "NDCC - Uluwatu 3",
        track: "fgd",
        description: isID
          ? "Presentasi & diskusi analisis kritis terhadap RUU Sisdiknas, dilanjutkan perumusan draft policy brief"
          : "Presentation and discussion on a critical analysis of the Sisdiknas Bill, followed by drafting a policy brief",
        speaker: isID
          ? "Ketua SAU UPI (Prof. Dr. Yadi Ruyadi, M.Si.)"
          : "Chairman of SAU UPI (Prof. Dr. Yadi Ruyadi, M.Si.)",
      },
      {
        name: isID ? "[FGD 4] MODEL PELAKSANAAN PPG YANG IDEAL" : "[FGD 4] IDEAL PPG IMPLEMENTATION MODEL",
        startTime: "08:30",
        endTime: "12:30",
        location: "NDCC - Uluwatu 4",
        track: "fgd",
        description: isID
          ? "Presentasi & diskusi model pelaksanaan PPG yang ideal, dilanjutkan perumusan draft policy brief"
          : "Presentation and discussion on the ideal PPG implementation model, followed by drafting a policy brief",
        speaker: isID
          ? "Direktur PPG (Ferry Maulana Putra, S.Pd., M.Ed.)"
          : "Director of PPG (Ferry Maulana Putra, S.Pd., M.Ed.)",
      },
      {
        name: isID ? "[FGD 5] FILOSOFI PENDIDIKAN INDONESIA" : "[FGD 5] PHILOSOPHY OF INDONESIAN EDUCATION",
        startTime: "08:30",
        endTime: "12:30",
        location: "NDCC - Uluwatu 5",
        track: "fgd",
        description: isID
          ? "Presentasi & diskusi menggali dan menguatkan filosofi pendidikan Indonesia sebagai dasar transformasi sistem pendidikan nasional, dilanjutkan perumusan draft policy brief"
          : "Presentation and discussion on exploring and strengthening the philosophy of Indonesian education as the basis for national education transformation, followed by drafting a policy brief",
        speaker: isID
          ? "Ketua Umum PP Muhammadiyah (Prof. Muhadjir Effendy)"
          : "General Chairman of PP Muhammadiyah (Prof. Muhadjir Effendy)",
      },
      {
        name: isID ? "[FGD 6] STRATEGI PENINGKATAN HASIL PISA" : "[FGD 6] STRATEGY TO IMPROVE PISA RESULTS",
        startTime: "08:30",
        endTime: "12:30",
        location: "NDCC - Uluwatu 6",
        track: "fgd",
        description: isID
          ? "Presentasi & diskusi strategi nasional peningkatan hasil PISA Indonesia melalui transformasi pembelajaran, penguatan kompetensi guru, dan reformasi asesmen, dilanjutkan perumusan draft policy brief"
          : "Presentation and discussion on the national strategy to improve Indonesia's PISA results through learning transformation, teacher competence strengthening, and assessment reform, followed by drafting a policy brief",
        speaker: isID
          ? "Staf Ahli Menteri Bidang Teknologi Pendidikan, Kemendikdasmen (Ir. Moch. Abduh, MS.Ed., Ph.D.)"
          : "Ministerial Expert Staff for Education Technology, Ministry of Primary and Secondary Education (Ir. Moch. Abduh, MS.Ed., Ph.D.)",
      },
      {
        name: isID ? "PERUMUSAN KONSENSUS BALI" : "FORMULATION OF THE BALI CONSENSUS",
        startTime: "13:30",
        endTime: "15:30",
        location: "NDCC - Uluwatu 2",
        track: "fgd",
        description: isID
          ? "Deklarasi, Rekomendasi, dan Piagam Bali"
          : "Declaration, Recommendations, and Bali Charter",
        speaker: isID ? "Tim FGD dan WR 1 LPTK" : "FGD Team and Vice Rector I of LPTKs",
      },
      {
        name: isID ? "RAPAT REKTOR PEMBAHASAN KONSESUS BALI" : "RECTORS' MEETING ON THE BALI CONSENSUS",
        startTime: "15:30",
        endTime: "16:30",
        location: "NDCC - Uluwatu 1",
        track: "fgd",
        speaker: isID ? "Rektor-rektor LPTK" : "Rectors of LPTKs",
      },
      {
        name: "LADIES PROGRAM",
        track: "ladies",
        startTime: "07:00",
        endTime: "17:00",
        location: isID ? "Taman Werdi Budaya Art Center & Wanaku Restaurant, Tuban" : "Taman Werdi Budaya Art Center & Wanaku Restaurant, Tuban",
        description: isID
          ? "Breakfast & persiapan di hotel (07.00-08.00), perjalanan ke Taman Werdi Budaya Art Center (08.00-09.00), berkegiatan di Taman Werdi Budaya Art Center (09.00-12.00), perjalanan ke Wanaku Restaurant Tuban (12.00-13.00), makan siang & ISHOMA (13.00-15.00), perjalanan kembali ke BNDCC (15.00-16.00), istirahat (16.00-17.00)"
          : "Breakfast & preparation at the hotel (07:00-08:00), travel to Taman Werdi Budaya Art Center (08:00-09:00), activities at Taman Werdi Budaya Art Center (09:00-12:00), travel to Wanaku Restaurant Tuban (12:00-13:00), lunch & ISHOMA (13:00-15:00), travel back to BNDCC (15:00-16:00), rest (16:00-17:00)",
      },
    ],
  },
  {
    date: "04-10-2026",
    dateFormatted: isID ? "Minggu, 4 Oktober 2026" : "Sunday, October 4, 2026",
    shortLabel: isID ? "Min, 4 Okt" : "Sun, Oct 4",
    activities: [
      {
        name: isID ? "PERSIAPAN KEBERANGKATAN" : "DEPARTURE PREPARATION",
        startTime: "06:00",
        endTime: "07:30",
        location: isID ? "Hotel Masing-masing" : "Respective Hotels",
      },
      {
        name: isID ? "KEBERANGKATAN KE TEMPAT CITY TOUR" : "DEPARTURE TO CITY TOUR LOCATIONS",
        startTime: "07:30",
        endTime: "08:00",
        location: isID ? "Hotel Masing-masing" : "Respective Hotels",
      },
      {
        name: isID ? "CITY TOUR" : "CITY TOUR",
        startTime: "08:00",
        endTime: "16:00",
        location: isID ? "Denpasar / Bali" : "Denpasar / Bali",
        description: isID
          ? "Tur kota Denpasar/Bali - mengenal budaya lokal (jadwal bersifat tentatif)"
          : "Denpasar/Bali city tour - exploring local culture (schedule is tentative)",
      },
    ],
  },
]

type Track = "main" | "fgd" | "ladies"
type DayStatus = "past" | "today" | "future"

type ActivityTheme = { dot: string; text: string }

const THEME_RULES: { test: (n: string) => boolean; dot: string; text: string }[] = [
  { test: (n) => n.includes("PEMBUKAAN") || n.includes("OPENING"), dot: "bg-blue-500", text: "text-blue-600 dark:text-blue-400" },
  { test: (n) => n.includes("KEYNOTE"), dot: "bg-amber-500", text: "text-amber-600 dark:text-amber-400" },
  { test: (n) => n.includes("PENDIDIKAN") || n.includes("EDUCATION"), dot: "bg-emerald-500", text: "text-emerald-600 dark:text-emerald-400" },
  { test: (n) => n.includes("GREEN"), dot: "bg-lime-500", text: "text-lime-600 dark:text-lime-400" },
  { test: (n) => n.includes("REVITALISASI") || n.includes("REVITALIZATION"), dot: "bg-cyan-500", text: "text-cyan-600 dark:text-cyan-400" },
  { test: (n) => n.includes("SINERGI") || n.includes("SYNERGY"), dot: "bg-violet-500", text: "text-violet-600 dark:text-violet-400" },
  { test: (n) => n.includes("TATA KELOLA") || n.includes("GOVERNANCE"), dot: "bg-pink-500", text: "text-pink-600 dark:text-pink-400" },
  { test: (n) => n.includes("ARAH KEBIJAKAN") || n.includes("POLICY"), dot: "bg-orange-500", text: "text-orange-600 dark:text-orange-400" },
  { test: (n) => n.includes("CONFERENCE"), dot: "bg-purple-500", text: "text-purple-600 dark:text-purple-400" },
  { test: (n) => n.includes("LADIES"), dot: "bg-rose-500", text: "text-rose-600 dark:text-rose-400" },
  { test: (n) => n.includes("PAMERAN") || n.includes("EXHIBITION"), dot: "bg-fuchsia-500", text: "text-fuchsia-600 dark:text-fuchsia-400" },
  { test: (n) => n.includes("CITY TOUR") || n.includes("SPORTS") || n.includes("OLAH RAGA"), dot: "bg-indigo-500", text: "text-indigo-600 dark:text-indigo-400" },
  { test: (n) => n.includes("KEDATANGAN") || n.includes("ARRIVAL"), dot: "bg-green-500", text: "text-green-600 dark:text-green-400" },
  { test: (n) => n.includes("FGD"), dot: "bg-teal-500", text: "text-teal-600 dark:text-teal-400" },
  { test: (n) => n.includes("LPTK"), dot: "bg-sky-500", text: "text-sky-600 dark:text-sky-400" },
  { test: (n) => n.includes("PENUTUPAN") || n.includes("CLOSING"), dot: "bg-red-500", text: "text-red-600 dark:text-red-400" },
  { test: (n) => n.includes("PENYAMPAIAN") || n.includes("PENYERAHAN") || n.includes("RESULTS") || n.includes("HANDOVER"), dot: "bg-red-400", text: "text-red-500 dark:text-red-400" },
  { test: (n) => n.includes("TARI") || n.includes("DANCE"), dot: "bg-amber-600", text: "text-amber-700 dark:text-amber-500" },
  { test: (n) => n.includes("REGISTRASI") || n.includes("REGISTRATION"), dot: "bg-slate-500", text: "text-slate-600 dark:text-slate-400" },
  { test: (n) => n.includes("KUNJUNGAN") || n.includes("VISIT"), dot: "bg-yellow-600", text: "text-yellow-600 dark:text-yellow-500" },
  { test: (n) => n.includes("KEBERANGKATAN") || n.includes("PERSIAPAN") || n.includes("DEPARTURE") || n.includes("PREPARATION"), dot: "bg-teal-600", text: "text-teal-700 dark:text-teal-500" },
  {
    test: (n) =>
      n.includes("ISTIRAHAT") || n.includes("KUDAPAN") || n.includes("MAKAN") || n.includes("BREAK") ||
      n.includes("REFRESHMENTS") || n.includes("DINNER") || n.includes("LUNCH") || n.includes("DISKUSI") ||
      n.includes("DISCUSSION") || n.includes("ISHOMA") || n.includes("REST"),
    dot: "bg-gray-400",
    text: "text-gray-600 dark:text-gray-400",
  },
]

const DEFAULT_THEME: ActivityTheme = { dot: "bg-gray-400", text: "text-gray-600 dark:text-gray-400" }

const getActivityTheme = (name: string): ActivityTheme => {
  const upper = name.toUpperCase()
  return THEME_RULES.find((rule) => rule.test(upper)) ?? DEFAULT_THEME
}

const toMinutes = (time: string): number => {
  const [h, m] = time.split(":").map(Number)
  return h * 60 + m
}

const parseDMY = (dateStr: string): Date => {
  const [d, m, y] = dateStr.split("-").map(Number)
  return new Date(y, m - 1, d)
}

const getDayStatus = (dateStr: string, now: Date): DayStatus => {
  const day = parseDMY(dateStr)
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const dayOnly = new Date(day.getFullYear(), day.getMonth(), day.getDate())
  if (dayOnly.getTime() < today.getTime()) return "past"
  if (dayOnly.getTime() === today.getTime()) return "today"
  return "future"
}

const getDefaultEventDate = (): string => {
  const days = getEventDays(true)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const upcoming = days.find((day) => {
    const d = parseDMY(day.date)
    const dOnly = new Date(d.getFullYear(), d.getMonth(), d.getDate())
    return dOnly.getTime() >= today.getTime()
  })
  return (upcoming ?? days[days.length - 1]).date
}

const HOUR_HEIGHT = 72

interface CalendarBlock {
  activity: Activity
  col: number
  totalCols: number
  visualMinutes: number
}

// Susun aktivitas ke dalam kolom-kolom agar sesi yang jamnya beririsan (mis. FGD paralel) tidak saling tumpuk
const layoutCalendarBlocks = (activities: Activity[]): CalendarBlock[] => {
  const sorted = [...activities].sort((a, b) => toMinutes(a.startTime) - toMinutes(b.startTime))
  let columnEnds: number[] = []
  let cluster: { activity: Activity; col: number }[] = []
  let clusterEnd = -Infinity
  const clusters: { activity: Activity; col: number }[][] = []

  const flush = () => {
    if (cluster.length) clusters.push(cluster)
    cluster = []
  }

  for (const activity of sorted) {
    const start = toMinutes(activity.startTime)
    const end = toMinutes(activity.endTime)
    if (start >= clusterEnd) {
      flush()
      columnEnds = []
      clusterEnd = -Infinity
    }
    let col = columnEnds.findIndex((e) => e <= start)
    if (col === -1) {
      col = columnEnds.length
      columnEnds.push(end)
    } else {
      columnEnds[col] = end
    }
    cluster.push({ activity, col })
    clusterEnd = Math.max(clusterEnd, end)
  }
  flush()

  const blocks: CalendarBlock[] = []
  for (const c of clusters) {
    const totalCols = Math.max(...c.map((x) => x.col)) + 1
    for (const x of c) blocks.push({ activity: x.activity, col: x.col, totalCols, visualMinutes: 0 })
  }

  // Batasi tinggi setiap blok agar tidak melewati jarak nyata ke sesi berikutnya di kolom yang sama,
  // supaya sesi-sesi pendek yang berdekatan (mis. rangkaian upacara pembukaan) tidak saling tumpuk
  const byColumn = new Map<number, CalendarBlock[]>()
  for (const b of blocks) {
    const arr = byColumn.get(b.col) ?? []
    arr.push(b)
    byColumn.set(b.col, arr)
  }
  for (const arr of byColumn.values()) {
    arr.sort((a, b) => toMinutes(a.activity.startTime) - toMinutes(b.activity.startTime))
    arr.forEach((block, i) => {
      const start = toMinutes(block.activity.startTime)
      const duration = toMinutes(block.activity.endTime) - start
      const next = arr[i + 1]
      const gapToNext = next ? toMinutes(next.activity.startTime) - start : duration
      block.visualMinutes = Math.max(1, Math.min(duration, gapToNext))
    })
  }

  return blocks
}

export default function JadwalKegiatan() {
  const { i18n } = useTranslation()
  const isID = i18n.language === 'id'
  const eventDays = useMemo(() => getEventDays(isID), [isID])

  const [expandedDate, setExpandedDate] = useState<string>(getDefaultEventDate)
  const [activeTrack, setActiveTrack] = useState<Track>("main")
  const [viewMode, setViewMode] = useState<"list" | "calendar">("list")
  const [now, setNow] = useState<Date>(() => new Date())

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60000)
    return () => clearInterval(timer)
  }, [])

  const selectedDay = useMemo(
    () => eventDays.find((d) => d.date === expandedDate) ?? eventDays[0],
    [eventDays, expandedDate]
  )

  const dayStatuses = useMemo(
    () => eventDays.map((day) => getDayStatus(day.date, now)),
    [eventDays, now]
  )

  const selectedStatus = dayStatuses[eventDays.findIndex((d) => d.date === expandedDate)] ?? "future"

  const availableTracks = useMemo(() => {
    const set = new Set<Track>(["main"])
    selectedDay.activities.forEach((a) => {
      if (a.track) set.add(a.track)
    })
    return Array.from(set)
  }, [selectedDay])

  useEffect(() => {
    if (!availableTracks.includes(activeTrack)) setActiveTrack("main")
  }, [availableTracks, activeTrack])

  const visibleActivities = useMemo(() => {
    return selectedDay.activities
      .filter((a) => (activeTrack === "main" ? !a.track : a.track === activeTrack))
      .slice()
      .sort((a, b) => toMinutes(a.startTime) - toMinutes(b.startTime))
  }, [selectedDay, activeTrack])

  const calendarBlocks = useMemo(() => layoutCalendarBlocks(visibleActivities), [visibleActivities])

  const gridBounds = useMemo(() => {
    if (visibleActivities.length === 0) return { startHour: 6, endHour: 22 }
    const starts = visibleActivities.map((a) => toMinutes(a.startTime))
    const ends = visibleActivities.map((a) => toMinutes(a.endTime))
    const startHour = Math.max(0, Math.floor(Math.min(...starts) / 60))
    const endHour = Math.min(24, Math.ceil(Math.max(...ends) / 60))
    return { startHour, endHour: Math.max(endHour, startHour + 1) }
  }, [visibleActivities])

  const handleSelectDate = (date: string) => {
    setExpandedDate(date)
    setActiveTrack("main")

    // Scroll ke posisi pill tanggal (bukan judul paling atas) supaya list hari yang lebih pendek
    // tidak "hilang" karena posisi scroll masih tertinggal jauh di bawah.
    // Pakai anchor statis, bukan elemen sticky, karena rect elemen sticky sudah terkunci saat stuck.
    const anchor = document.getElementById("timeline-scroll-anchor")
    if (anchor) {
      const y = anchor.getBoundingClientRect().top + window.scrollY - 72
      window.scrollTo({ top: y, behavior: "smooth" })
    }
  }

  const handleAddToCalendar = (activity: Activity, dateStr: string) => {
    const [day, month, year] = dateStr.split('-')
    const formattedDate = `${year}${month}${day}`
    const formatTime = (time: string) => time.replace(':', '') + '00'
    const startDateTime = `${formattedDate}T${formatTime(activity.startTime)}`
    const endDateTime = `${formattedDate}T${formatTime(activity.endTime)}`
    const title = encodeURIComponent(`KONASPI XII: ${activity.name}`)
    const details = encodeURIComponent(`${activity.description || ''} \n\n${isID ? 'Pembicara' : 'Speaker'}: ${activity.speaker || '-'}`)
    const location = encodeURIComponent(activity.location || 'NDCC Bali')
    const gCalUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${startDateTime}/${endDateTime}&details=${details}&location=${location}`
    window.open(gCalUrl, '_blank')
  }

  const trackLabel = (t: Track) => {
    if (t === "main") return isID ? "Sesi Utama" : "Main Session"
    if (t === "fgd") return "FGD"
    return isID ? "Program Ladies" : "Ladies Program"
  }

  const dayBadge = (status: DayStatus) => {
    if (status === "past") return { text: isID ? "Selesai" : "Done", className: "bg-gray-500/15 text-gray-500 dark:text-gray-400" }
    if (status === "today") return { text: isID ? "Hari Ini" : "Today", className: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400" }
    return { text: isID ? "Akan Datang" : "Upcoming", className: "bg-blue-500/15 text-blue-600 dark:text-blue-400" }
  }

  const badge = dayBadge(selectedStatus)
  const nowMinutes = now.getHours() * 60 + now.getMinutes()

  return (
    <section id="timeline" className="relative py-12 bg-slate-50 dark:bg-gray-950">

      {/* Background Pattern */}
      <div className="absolute inset-0 z-0 opacity-30 pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(#3b82f6_1px,transparent_1px)] [background-size:24px_24px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]"></div>
      </div>

      <div className="w-full py-8 md:py-12 px-4 md:px-8 relative z-10">
        <div className="container mx-auto px-4 max-w-4xl py-0">

          <div className="mb-8 text-center">
            <div className="inline-block mb-4">
              <div className="flex items-center justify-center gap-2 px-4 py-2 rounded-full bg-blue-100 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800">
                <Calendar className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                <span className="text-sm font-bold text-blue-600 dark:text-blue-400">
                  {isID ? "RUNDOWN ACARA" : "EVENT SCHEDULE"}
                </span>
              </div>
            </div>
            <h1 className="text-3xl md:text-5xl font-bold mb-4 text-gray-900 dark:text-white">
              {isID ? "Jadwal Kegiatan" : "Event Schedule"}
            </h1>
            <p className="text-base md:text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              {isID
                ? "Rangkaian acara Konvensi Nasional Pendidikan XII di Nusa Dua Convention Center (NDCC), Bali."
                : "Series of events for the XII National Education Convention at Nusa Dua Convention Center (NDCC), Bali."}
            </p>
          </div>

          {/* Anchor statis (non-sticky) untuk target scroll - jangan pakai elemen sticky sebagai target,
              karena getBoundingClientRect()-nya sudah terkunci ke posisi stuck, bukan posisi asli di halaman */}
          <div id="timeline-scroll-anchor" />

          {/* Day pill selector - melayang di bawah navbar saat scroll */}
          <div className="sticky top-[76px] sm:top-[92px] z-40 -mx-4 px-4 sm:mx-0 sm:px-0 py-3 mb-5 bg-slate-50/90 dark:bg-gray-950/90 backdrop-blur-md shadow-[0_4px_12px_-4px_rgba(0,0,0,0.08)] dark:shadow-[0_4px_12px_-4px_rgba(0,0,0,0.4)] rounded-b-xl">
            <div className="flex gap-2 overflow-x-auto pt-2 pb-2 pl-1 pr-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {eventDays.map((day, i) => {
                const status = dayStatuses[i]
                const selected = day.date === expandedDate
                return (
                  <button
                    key={day.date}
                    onClick={() => handleSelectDate(day.date)}
                    className={`relative shrink-0 px-4 py-2.5 rounded-xl border text-sm font-semibold transition-all
                      ${selected
                        ? "bg-blue-600 border-blue-600 text-white shadow-md"
                        : status === "past"
                          ? "bg-white/60 dark:bg-gray-800/40 border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-500"
                          : "bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:border-blue-400"
                      }`}
                  >
                    {status === "today" && (
                      <span className={`absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse ring-2 ${selected ? "ring-blue-600" : "ring-slate-50 dark:ring-gray-950"}`} />
                    )}
                    <span className="whitespace-nowrap">{day.shortLabel}</span>
                  </button>
                )
              })}
            </div>
          </div>

          {/* Selected day header */}
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div>
              <h2 className="text-lg md:text-2xl font-bold text-gray-900 dark:text-white">{selectedDay.dateFormatted}</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {visibleActivities.length} {isID ? "sesi kegiatan" : "activity sessions"}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className={`px-3 py-1 rounded-full text-xs font-bold ${badge.className}`}>
                {badge.text}
              </span>
              <div className="flex items-center gap-1 p-1 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => setViewMode("list")}
                  title={isID ? "Mode List" : "List View"}
                  className={`p-1.5 rounded-md transition-colors ${viewMode === "list" ? "bg-blue-600 text-white" : "text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"}`}
                >
                  <List className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode("calendar")}
                  title={isID ? "Mode Kalender" : "Calendar View"}
                  className={`p-1.5 rounded-md transition-colors ${viewMode === "calendar" ? "bg-blue-600 text-white" : "text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"}`}
                >
                  <LayoutGrid className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Track tabs (only when a day has parallel tracks) */}
          {availableTracks.length > 1 && (
            <div className="flex gap-2 mb-5 flex-wrap">
              {availableTracks.map((t) => (
                <button
                  key={t}
                  onClick={() => setActiveTrack(t)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wide transition-colors
                    ${activeTrack === t
                      ? "bg-blue-600 text-white"
                      : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:border-blue-400"
                    }`}
                >
                  {trackLabel(t)}
                </button>
              ))}
            </div>
          )}
          {availableTracks.length > 1 && activeTrack !== "main" && (
            <p className="text-xs text-gray-500 dark:text-gray-400 -mt-3 mb-4">
              {isID
                ? "Sesi ini berjalan paralel dengan Sesi Utama pada jam yang sama."
                : "This session runs in parallel with the Main Session at the same time."}
            </p>
          )}

          {/* Timeline list */}
          {viewMode === "list" && (
          <div className="relative space-y-3 pb-4">
            {visibleActivities.map((activity, idx) => {
              const theme = getActivityTheme(activity.name)
              const isOngoing =
                selectedStatus === "today" &&
                nowMinutes >= toMinutes(activity.startTime) &&
                nowMinutes < toMinutes(activity.endTime)

              return (
                <div key={idx} className="relative flex gap-3 sm:gap-4">
                  {/* Time column */}
                  <div className="w-12 sm:w-16 shrink-0 text-right pt-3.5">
                    <p className="text-xs sm:text-sm font-mono font-bold text-gray-700 dark:text-gray-200">{activity.startTime}</p>
                    <p className="text-[10px] sm:text-xs font-mono text-gray-400">{activity.endTime}</p>
                  </div>

                  {/* Connector */}
                  <div className="relative shrink-0 flex flex-col items-center pt-4">
                    <span className={`block w-2.5 h-2.5 rounded-full ${theme.dot} ${isOngoing ? "animate-pulse ring-4 ring-emerald-400/30" : ""}`} />
                    {idx < visibleActivities.length - 1 && (
                      <span className="w-px flex-1 bg-gray-200 dark:bg-gray-700 mt-1" />
                    )}
                  </div>

                  {/* Content */}
                  <Card className={`flex-1 min-w-0 p-3 sm:p-4 mb-1 transition-shadow hover:shadow-md ${isOngoing ? "border-emerald-400 dark:border-emerald-500 ring-1 ring-emerald-400/40" : "border-gray-200 dark:border-gray-700"}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <h3 className={`font-bold text-sm sm:text-base leading-snug ${theme.text}`}>{activity.name}</h3>
                          {isOngoing && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 shrink-0">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                              {isID ? "BERLANGSUNG" : "LIVE"}
                            </span>
                          )}
                        </div>
                        {activity.description && (
                          <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mb-1.5 leading-relaxed">{activity.description}</p>
                        )}
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
                          {activity.location && (
                            <span className="inline-flex items-center gap-1">
                              <MapPin className="w-3.5 h-3.5 shrink-0" />
                              {activity.location}
                            </span>
                          )}
                          {activity.speaker && (
                            <span className="inline-flex items-center gap-1">
                              <Users className="w-3.5 h-3.5 shrink-0" />
                              {activity.speaker}
                            </span>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => handleAddToCalendar(activity, selectedDay.date)}
                        title={isID ? "Simpan ke Google Calendar" : "Save to Google Calendar"}
                        className="shrink-0 p-1.5 rounded-full text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-gray-700 transition-colors"
                      >
                        <CalendarPlus className="w-4 h-4" />
                      </button>
                    </div>
                  </Card>
                </div>
              )
            })}
          </div>
          )}

          {/* Calendar view */}
          {viewMode === "calendar" && (
            <div className="overflow-x-auto pb-4 -mx-4 px-4 sm:mx-0 sm:px-0">
              <div className="flex min-w-[420px] border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-900 shadow-sm">
                {/* Hour labels */}
                <div
                  className="w-12 sm:w-14 shrink-0 relative border-r border-gray-200 dark:border-gray-700"
                  style={{ height: (gridBounds.endHour - gridBounds.startHour) * HOUR_HEIGHT }}
                >
                  {Array.from({ length: gridBounds.endHour - gridBounds.startHour + 1 }, (_, i) => gridBounds.startHour + i).map((h) => (
                    <span
                      key={h}
                      className="absolute left-0 right-1 text-right text-[10px] text-gray-400 -translate-y-1/2"
                      style={{ top: (h - gridBounds.startHour) * HOUR_HEIGHT }}
                    >
                      {String(h).padStart(2, "0")}:00
                    </span>
                  ))}
                </div>

                {/* Grid area */}
                <div
                  className="relative flex-1"
                  style={{ height: (gridBounds.endHour - gridBounds.startHour) * HOUR_HEIGHT }}
                >
                  {Array.from({ length: gridBounds.endHour - gridBounds.startHour + 1 }, (_, i) => gridBounds.startHour + i).map((h) => (
                    <div
                      key={h}
                      className="absolute left-0 right-0 border-t border-gray-100 dark:border-gray-800"
                      style={{ top: (h - gridBounds.startHour) * HOUR_HEIGHT }}
                    />
                  ))}

                  {selectedStatus === "today" && nowMinutes >= gridBounds.startHour * 60 && nowMinutes <= gridBounds.endHour * 60 && (
                    <div
                      className="absolute left-0 right-0 flex items-center z-20 pointer-events-none"
                      style={{ top: (nowMinutes - gridBounds.startHour * 60) / 60 * HOUR_HEIGHT }}
                    >
                      <span className="w-2 h-2 rounded-full bg-red-500 shrink-0 -ml-1" />
                      <span className="flex-1 h-px bg-red-500" />
                    </div>
                  )}

                  {calendarBlocks.map(({ activity, col, totalCols, visualMinutes }, idx) => {
                    const theme = getActivityTheme(activity.name)
                    const startMin = toMinutes(activity.startTime)
                    const endMin = toMinutes(activity.endTime)
                    const top = (startMin - gridBounds.startHour * 60) / 60 * HOUR_HEIGHT
                    // Tinggi dibatasi ke visualMinutes (jarak nyata ke sesi berikutnya) supaya tidak tumpang tindih
                    const height = Math.max(visualMinutes / 60 * HOUR_HEIGHT - 2, 5)
                    const widthPct = 100 / totalCols
                    const leftPct = col * widthPct
                    const isOngoing =
                      selectedStatus === "today" && nowMinutes >= startMin && nowMinutes < endMin

                    return (
                      <div
                        key={idx}
                        title={`${activity.name} (${activity.startTime}-${activity.endTime})${activity.location ? ` — ${activity.location}` : ""}`}
                        className={`absolute rounded-md bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 shadow-sm hover:shadow-md hover:z-30 transition-shadow overflow-hidden pl-2 pr-1.5 ${height >= 30 ? "py-1" : "py-0.5"} ${isOngoing ? "ring-2 ring-emerald-400/60" : ""}`}
                        style={{
                          top,
                          height,
                          left: `calc(${leftPct}% + 2px)`,
                          width: `calc(${widthPct}% - 4px)`,
                        }}
                      >
                        <span className={`absolute inset-y-0 left-0 w-[3px] ${theme.dot}`} />
                        <p className={`text-[10px] sm:text-xs font-bold leading-tight truncate ${theme.text}`}>{activity.name}</p>
                        {height >= 30 && (
                          <p className="text-[9px] sm:text-[10px] text-gray-400 truncate">{activity.startTime}-{activity.endTime}</p>
                        )}
                        {activity.location && height >= 54 && (
                          <p className="text-[9px] sm:text-[10px] text-gray-400 truncate hidden sm:block">{activity.location}</p>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
              <p className="text-xs text-gray-400 mt-2">
                {isID ? "Arahkan kursor ke sesi singkat untuk lihat detailnya. Klik ikon list untuk detail lengkap." : "Hover a short session to see its details. Switch to list view for full details."}
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  )
}
